import {Key, ReactNode} from 'react';
import {NavigateFunction, NavigateOptions} from 'react-router-dom';
import {ItemType} from 'antd/es/menu/interface';
import {UploadRequestOption} from 'rc-upload/lib/interface';
import {MenuItem, ResultDocument, TZoomType} from '@/types';
import {
  DEFAULT_VALUE,
  DETECTION_KEY,
  HTTP_CODE,
  TEMPLATE_TYPE,
  TOOLBAR_VARIABLES,
  ZOOM_TYPE,
} from '@/constants';
import {i18nKey} from '@/locales/i18n';

/**
 * @param {string} text - The text to remove the last character from.
 * @param {string} character  - The character to remove.
 * @returns {string} The text with the last character removed.
 */
export const removeLastCharacter = (
  text: string,
  character: string,
): string => {
  if (!text) {
    return '';
  }
  return text.charAt(text.length - 1) === character ? text.slice(0, -1) : text;
};

/**
 * @param {ArrayBuffer} arrayBuffer - The array buffer of the file.
 * @returns {string} The mime type of the file.
 */
export const getMimeTypeFromArrayBuffer = (
  arrayBuffer: ArrayBuffer,
): string => {
  const uint8arr = new Uint8Array(arrayBuffer);

  const len = 4;
  if (uint8arr.length >= len) {
    const signatureArr = new Array({length: len});
    for (let i = 0; i < len; i++)
      signatureArr[i] = new Uint8Array(arrayBuffer)[i].toString(16);
    const signature = signatureArr.join('').toUpperCase();

    switch (signature) {
      case '89504E47':
        return 'image/png';
      case '47494638':
        return 'image/gif';
      case '25504446':
        return 'application/pdf';
      case 'FFD8FFDB':
      case 'FFD8FFE0':
        return 'image/jpeg';
      case '504B0304':
        return 'application/zip';
      default:
        return 'image/jpeg';
    }
  }
  return '';
};

/**
 * Get a menu item.
 * @param {ReactNode | null} [label] - The label of the menu item.
 * @param {Key} [key] - The unique key for the menu item.
 * @param {ReactNode} [icon] - The icon associated with the menu item.
 * @param {ItemType[]} [children] - The child items of the menu item.
 * @param {'item' | 'group' | 'divider'} [type='item'] - The type of the menu item.
 * @param {() => void} [onClick] - The click event handler for the menu item.
 * @returns {MenuItem} The created menu item.
 */
export const getItem = ({
  label = null,
  key,
  icon,
  children,
  type = 'item',
  onClick,
  hidden = false,
}: {
  label?: ReactNode | null;
  key?: Key;
  icon?: ReactNode;
  children?: ItemType[];
  type?: 'item' | 'group' | 'divider';
  onClick?: () => void;
  hidden?: boolean;
}): MenuItem => {
  return {
    key,
    icon,
    children,
    label,
    type,
    onClick,
    hidden,
  } as MenuItem;
};

/**
 * Get the current zoom level from a zoom percentage string.
 * @param {string} zoom - The zoom percentage string (e.g., '100%').
 * @returns {number} The zoom level as a number.
 */
export const getCurrentZoom = (zoom: string): number => {
  return Number(zoom.replace('%', '')) || TOOLBAR_VARIABLES.scale_default;
};

/**
 * Get the new zoom level based on the current zoom and zoom type.
 * @param {number} currentZoom - The current zoom level.
 * @param {TZoomType} type - The type of zoom operation ('zoom_in' or 'zoom_out').
 * @returns {number | null} The new zoom level or null if invalid.
 */
export const getNewZoom = (
  currentZoom: number,
  type: TZoomType,
): number | null => {
  let newZoom;

  if (type === ZOOM_TYPE.zoom_in) {
    const nextZoomLevel = TOOLBAR_VARIABLES.validZoomLevels.find(
      (level) => level > currentZoom,
    );
    newZoom = nextZoomLevel
      ? Math.min(nextZoomLevel, TOOLBAR_VARIABLES.max_zoom)
      : currentZoom;
  } else if (type === ZOOM_TYPE.zoom_out) {
    const prevZoomLevel = [...TOOLBAR_VARIABLES.validZoomLevels]
      .reverse()
      .find((level) => level < currentZoom);
    newZoom = prevZoomLevel
      ? Math.max(prevZoomLevel, TOOLBAR_VARIABLES.min_zoom)
      : currentZoom;
  } else {
    return null;
  }

  return newZoom;
};

/**
 * @param {ResultDocument[]} r - The result document array.
 * @param {number} totalPage - The total number of pages.
 * @returns {string[]} - The page numbers.
 */
export const getPageNumberMatching = (
  r: ResultDocument[],
  totalPage: number,
) => {
  const pageNumbers = r.map((item, index) => {
    const pageNumber = item?.data?.fields[DEFAULT_VALUE.value]?.page_number;
    return {
      tabIndex: index,
      pageNumber,
    };
  });

  const validPageNumbers = pageNumbers.filter(
    (item) => item.pageNumber !== undefined,
  );

  const ranges = validPageNumbers.map((item, index) => {
    const nextPageNumber =
      validPageNumbers[index + TOOLBAR_VARIABLES.min_page]?.pageNumber ??
      totalPage + TOOLBAR_VARIABLES.min_page;
    return {
      tabIndex: item.tabIndex,
      startPage: item.pageNumber,
      endPage: nextPageNumber - TOOLBAR_VARIABLES.min_page,
    };
  });

  return ranges;
};

/**
 * @param {DETECTION_KEY} direction - The direction to navigate.
 * @param {Number} page - The current page.
 * @param {number} totalPage - The total number of pages.
 * @param {Array<{startPage: number; endPage: number; tabIndex: number}>} pageNumberMatching
 * @param {(tabIndex: string) => void} setActiveTab - The function to set the active tab.
 * @param {(newPage: number)=> void} setPage - The function to set the page.
 * @param {(newPage: number)=> void} setPageTemp - The function to set the page.
 * @returns {void}
 */
export const navigatePage = (
  direction: DETECTION_KEY,
  page: number,
  totalPage: number,
  pageNumberMatching: {startPage: number; endPage: number; tabIndex: number}[],
  setActiveTab: (tabIndex: string) => void,
  setPage: (newPage: number) => void,
  setPageTemp: (newPageTemp: number) => void,
): void => {
  let newPage = page;

  if (direction === DETECTION_KEY.next) {
    if (page < totalPage) {
      newPage = page + TOOLBAR_VARIABLES.min_page;
    }
  } else if (direction === DETECTION_KEY.previous) {
    if (page > TOOLBAR_VARIABLES.min_page) {
      newPage = page - TOOLBAR_VARIABLES.min_page;
    }
  } else if (direction === DETECTION_KEY.last) {
    newPage = totalPage;
  } else if (direction === DETECTION_KEY.first) {
    newPage = TOOLBAR_VARIABLES.min_page;
  }

  // const findMatching = pageNumberMatching.find((p) => {
  //   if (
  //     direction === DETECTION_KEY.next ||
  //     direction === DETECTION_KEY.previous
  //   ) {
  //     return p.startPage <= newPage && p.endPage >= newPage;
  //   }
  //   if (direction === DETECTION_KEY.last) {
  //     return p.endPage === totalPage;
  //   }
  //   if (direction === DETECTION_KEY.first) {
  //     return p.startPage === TOOLBAR_VARIABLES.min_page;
  //   }
  // });

  // if (findMatching) {
  //   setActiveTab(findMatching.tabIndex.toString());
  // }

  setPage(newPage);
  setPageTemp(newPage);
};

/**
 * @param {UploadRequestOption} options - The upload request options.
 * @returns {void}
 */
export const handleCustomRequest = (options: UploadRequestOption): void => {
  const {file, onProgress, onSuccess, onError} = options;

  if (file instanceof File) {
    const totalSize = file.size;
    const chunkSize = 1024 * 20;
    let uploaded = 0;
    let currentPosition = 0;

    const readNextChunk = () => {
      const reader = new FileReader();

      const chunk = file.slice(currentPosition, currentPosition + chunkSize);

      reader.onload = () => {
        uploaded += chunk.size;
        currentPosition += chunk.size;
        const percent = Math.min((uploaded / totalSize) * 100, 100);
        onProgress?.({percent});

        if (uploaded < totalSize) {
          readNextChunk();
        } else {
          onSuccess?.({status: HTTP_CODE.OK}, file);
        }
      };

      reader.onerror = () => {
        onError?.(new Error('Upload failed'));
      };

      reader.readAsArrayBuffer(chunk);
    };

    readNextChunk();
  } else {
    onError?.(new Error('The provided file is not valid.'));
  }
};

/**
 * @param {ReactNode} children - The children of the tab template.
 * @returns
 */
export const getItemsTabTemplate = (children: ReactNode) => [
  {
    key: TEMPLATE_TYPE.template,
    label: i18nKey.templateScreen.tab.template,
    children,
  },
  {
    key: TEMPLATE_TYPE.document_set,
    label: i18nKey.templateScreen.tab.documentSet,
    children,
  },
];

/**
 * @param {string} text - The text to remove Vietnamese Tones.
 * @returns
 */
export const removeVietnameseTones = (str: string) =>
  str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D');

type QueryParams = Record<string, string | number | undefined>;

/**
 * Navigate to a route with dynamic query parameters.
 * @param {NavigateFunction} navigate - The navigate function.
 * @param {string} pageRoute - The page route.
 * @param {QueryParams} [queryParams] - Additional query parameters (including page).
 * @param {NavigateOptions} [options] - The navigate options.
 * @returns {void}
 */
export const searchQuery = (
  navigate: NavigateFunction,
  pageRoute: string,
  queryParams: QueryParams = {},
  options?: NavigateOptions,
): void => {
  const params = new URLSearchParams(
    Object.entries(queryParams)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => [key, String(value)]),
  );

  const fullRoute = `${pageRoute}?${params.toString()}`;
  navigate(fullRoute, options);
};

/**
 * Get query parameter value as a number.
 * @param {string} key - The query parameter key.
 * @returns {number | undefined} - The numeric value of the query parameter or the default value.
 */
export const getNumericParam = (key: string): number | undefined => {
  const params = new URLSearchParams(window.location.search);
  const value = params.get(key);
  const numericValue = Number(value);

  // Check if value is valid number and not equal to 0
  if (!value || isNaN(numericValue)) {
    return undefined;
  }

  return numericValue;
};
/**
 * Get query parameter value as a string.
 *
 * @param {string} key - The query parameter key.
 * @returns {string | undefined} - The value of the query parameter or undefined.
 */
export const getStringParam = (key: string): string | undefined => {
  const params = new URLSearchParams(window.location.search);
  const value = params.get(key);
  return value && value.trim() !== '' ? value : undefined;
};

/**
 * Determines the sorting order based on the provided key and sort value.
 *
 * @param {string | undefined} key - The sorting key.
 * @param {string | undefined} sort - The sorting order value.
 * @returns {'ascend' | 'descend' | undefined} - The sorting direction or undefined if not matched.
 */
export const getSortDirection = (
  key?: string,
  sort?: string,
): 'ascend' | 'descend' | undefined => {
  if (!key || !sort) return undefined;
  if (sort === key) return 'ascend';
  if (sort === `-${key}`) return 'descend';
  return undefined;
};

export const formatNumber = (number?: number) => {
  if (!number) return;
  return new Intl.NumberFormat('de-DE').format(number);
};
